import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import IconButton from '@material-ui/core/IconButton'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import DescriptionIcon from '@material-ui/icons/Description'
import NoteAddIcon from '@material-ui/icons/NoteAdd'
import Tooltip from '../../../control/tooltip/tooltip'
import useTranslate from '../../../i18n/useTranslate'

export type TabKey = 'agenda' | 'minutes'

export type SideNavClassKey =
    | 'drawer'
    | 'paperLike'
    | 'drawerOpen'
    | 'drawerClose'
    | 'toolbar'
    | 'noDrawerBorder'
    | 'listItemRoot'
    | 'uploadFileRoot'
    | 'selected'
    | 'iconColor'

export type SideNavClasses = Partial<Record<SideNavClassKey, string>>

type Props = {
    open: boolean
    tab: TabKey
    onToggle: () => void
    onSelect: (t: TabKey) => void
    classes?: SideNavClasses
    canUseAgendaReport?: boolean
}

const drawerWidth = 240

const useStyles = makeStyles((theme) => ({
    drawer: { width: drawerWidth, flexShrink: 0, whiteSpace: 'nowrap' },
    paperLike: { position: 'unset', overflowY: 'unset' },
    drawerOpen: {
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerClose: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: theme.spacing(7) + 1,
        [theme.breakpoints.up('sm')]: { width: theme.spacing(9) + 1 },
    },
    toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', ...theme.mixins.toolbar },
    noDrawerBorder: { borderRight: 'none !important' },

    listItemRoot: {
        padding: '8px 16px',
        color: 'white',
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRight: 0,
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        marginBottom: 10,
        width: '100%',
        marginLeft: 15,
        '&:hover': { backgroundColor: '#fcfcfcff' },
        '&$selected': { backgroundColor: '#ffffff', marginLeft: 0, '&:hover': { backgroundColor: '#fcfcfcff' } },
    },
    uploadFileRoot: {
        padding: '8px 16px',
        color: 'white',
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRight: 0,
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        marginBottom: 10,
        width: '100%',
        marginLeft: 15,
        '&:hover': { backgroundColor: '#fcfcfcff' },
        '&$selected': { backgroundColor: '#ffffff', marginLeft: 0, '&:hover': { backgroundColor: '#fcfcfcff' } },
    },
    selected: {},
    iconColor: { color: '#8D8D8D' },
}))

export default function SideNav({
    open,
    tab,
    onToggle,
    onSelect,
    classes: classesOverride = {},
    canUseAgendaReport,
}: Props) {
    const classes = useStyles()
    const cx = { ...classes, ...classesOverride } as Record<SideNavClassKey, string>
    const t = useTranslate()
    return (
        <Drawer
            variant="permanent"
            className={clsx(cx.drawer, { [cx.drawerOpen]: open, [cx.drawerClose]: !open })}
            classes={{
                paper: clsx(cx.paperLike, { [cx.drawerOpen]: open, [cx.drawerClose]: !open }),
                paperAnchorDockedLeft: cx.noDrawerBorder,
            }}
        >
            <div className={cx.toolbar}>
                <IconButton onClick={onToggle}>{open ? <ChevronLeftIcon /> : <ChevronRightIcon />}</IconButton>
            </div>

            <List style={{ overflow: 'hidden' }}>
                <Tooltip title={t.get('UM.SETTING_AGENDA.TAB.AGENDA')} placement={open ? 'top' : 'right'}>
                    <ListItem
                        button
                        onClick={() => onSelect('agenda')}
                        selected={tab === 'agenda'}
                        classes={{ root: cx.listItemRoot, selected: cx.selected }}
                    >
                        <ListItemIcon className={cx.iconColor} style={{ minWidth: 60 }}>
                            <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary={t.get('UM.SETTING_AGENDA.TAB.AGENDA')}
                            style={{ margin: 0, color: '#58595B' }}
                        />
                    </ListItem>
                </Tooltip>

                <Tooltip title={t.get('UM.SETTING_AGENDA.TAB.MINUTES')} placement={open ? 'top' : 'right'}>
                    <ListItem
                        button
                        onClick={() => onSelect('minutes')}
                        selected={tab === 'minutes'}
                        classes={{ root: cx.uploadFileRoot, selected: cx.selected }}
                    >
                        <ListItemIcon className={cx.iconColor} style={{ minWidth: 60 }}>
                            {canUseAgendaReport ? <DescriptionIcon /> : <NoteAddIcon />}
                        </ListItemIcon>
                        <ListItemText
                            primary={t.get('UM.SETTING_AGENDA.TAB.MINUTES')}
                            style={{ margin: 0, color: '#58595B' }}
                        />
                    </ListItem>
                </Tooltip>
            </List>
        </Drawer>
    )
}
